import type { SourceFile } from 'ts-components/PiChat/protocol';

export type TreeFile = SourceFile | { path: string; content?: undefined };

export interface FileTreeFile {
  kind: 'file';
  name: string;
  displayPath: string;
  path: string;
}

export interface FileTreeFolder {
  kind: 'folder';
  name: string;
  displayPath: string;
  children: FileTreeNode[];
}

export type FileTreeNode = FileTreeFile | FileTreeFolder;

const segmentsOf = (path: string) => path.split('/').filter(Boolean);

const HIDDEN_DIRS = new Set(['.github']);

export const commonDirPrefix = (paths: string[]): string => {
  if (paths.length === 0) return '';
  const dirs = paths.map((path) => segmentsOf(path).slice(0, -1));
  const [first, ...rest] = dirs;
  let length = first.length;
  for (const dir of rest) {
    let i = 0;
    while (i < length && i < dir.length && dir[i] === first[i]) i += 1;
    length = i;
  }
  return length > 0 ? `${first.slice(0, length).join('/')}/` : '';
};

const compareNodes = (a: FileTreeNode, b: FileTreeNode) => {
  if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
  if (a.name === b.name) return 0;
  return a.name < b.name ? -1 : 1;
};

const sortTree = (nodes: FileTreeNode[]): FileTreeNode[] =>
  nodes
    .map((node) =>
      node.kind === 'folder'
        ? { ...node, children: sortTree(node.children) }
        : node
    )
    .sort(compareNodes);

export const visibleFiles = <T extends TreeFile>(files: T[]): T[] =>
  files.filter(
    (file) => !segmentsOf(file.path).some((segment) => HIDDEN_DIRS.has(segment))
  );

export const buildFileTree = (files: TreeFile[]): FileTreeNode[] => {
  const prefix = commonDirPrefix(files.map((file) => file.path));
  const prefixSegments = segmentsOf(prefix).length;
  const root: FileTreeFolder = {
    kind: 'folder',
    name: '',
    displayPath: '',
    children: [],
  };

  for (const file of files) {
    const segments = segmentsOf(file.path).slice(prefixSegments);
    if (segments.length === 0) continue;
    let parent = root;
    for (let i = 0; i < segments.length - 1; i += 1) {
      const displayPath = segments.slice(0, i + 1).join('/');
      let folder = parent.children.find(
        (node): node is FileTreeFolder =>
          node.kind === 'folder' && node.name === segments[i]
      );
      if (!folder) {
        folder = {
          kind: 'folder',
          name: segments[i],
          displayPath,
          children: [],
        };
        parent.children.push(folder);
      }
      parent = folder;
    }
    parent.children.push({
      kind: 'file',
      name: segments[segments.length - 1],
      displayPath: segments.join('/'),
      path: file.path,
    });
  }

  return sortTree(root.children);
};

export const toQaIdSegment = (path: string) =>
  path
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const filesInTreeOrder = (nodes: FileTreeNode[]): FileTreeFile[] =>
  nodes.flatMap((node) =>
    node.kind === 'file' ? [node] : filesInTreeOrder(node.children)
  );

const PREFERRED_FILE_NAME = 'script.js';

export const defaultFilePath = (nodes: FileTreeNode[]): string | null => {
  const files = filesInTreeOrder(nodes);
  const preferred = files
    .filter((file) => file.name === PREFERRED_FILE_NAME)
    .sort(
      (a, b) =>
        a.displayPath.split('/').length - b.displayPath.split('/').length
    )[0];
  return (preferred ?? files[0])?.path ?? null;
};
